import {
    attachContactInfo,
    IWorkplaceData,
    mapWorkplaces,
    Workplace,
} from './Workplace'
import { IPositionData, mapPositions, Position } from './Position'
import {
    mapTypeOfRepresentatives,
    TypeOfRepresentative,
    TypeOfRepresentativeData,
} from './TypeOfRepresentative'
import { Department } from './Department'
import { Contact, IContactData, mapContacts } from './Contact'
import { formatDate } from '../utils/format'

export type FunctionField = 'Ret' | 'Slet' | 'IngenRettelse'

export type IMemberData = {
    Arbejdssteder: IWorkplaceData[]
    ErFavorit: boolean
    AntalNoter: number
    OpdaterStatus: string
    Stillinger: IPositionData[]
    Medlemsident: number
    Medlemsnummer: string
    Fornavn: string
    Efternavn: string
    FuldeNavn: string
    Tillidshverv: TypeOfRepresentativeData[]
    ErEgetMedlem: boolean
    ErAktivtTilfoejetAfBruger: boolean
    Alder: number
    Afdeling: {
        Navn: string
        Nummer: string
        Indmeldelsesdato: string
    }
    ProfilBillede: { Content: string; MimeType: string }
    KontaktoplysningerList: IContactData[]
    FoedselsDato: string
}

export type IUpdateMemberData = {
    KontaktPrivat?: {
        Fastnet?: string
        Mobil?: string
        Email?: string
        OpdaterFunktionFastnet?: FunctionField
        OpdaterFunktionMobil?: FunctionField
        OpdaterFunktionEmail?: FunctionField
    }
    KontaktArbejde?: {
        Telefon?: string
        Email?: string
        OpdaterFunktionTelefon?: FunctionField
        OpdaterFunktionEmail?: FunctionField
    }
    Stillingsnummer?: string
    Timetal?: number
    Aendringsdato?: string
    OevrigeOplysninger?: string
    Arbejdsstednummer?: number
    Medlemsident: number
}

export type ITransferMemberToOtherWorkplace = {
    GammeltArbejdsstednummer?: number
    NytArbejdsstednummer?: number
    Stillingsnummer?: number
    Timetal?: number
    Aendringsdato?: string
    OevrigeOplysninger?: string
    Medlemsident: number
}

export type IRemoveMemberFromOwnList = {
    Aendringsdato?: string
    OevrigeOplysninger?: string
    Arbejdsstednummer?: number
    Medlemsident: number
}

export type ICancelWorkplace = {
    Arbejdsstedsophoersdato?: string
    OevrigeOplysninger?: string
    Arbejdsstednummer?: number
    Medlemsident: number
}

export function findPrimaryWorkPlace(member: Member): Workplace {
    if (member.allWorkPlaces.length > 1) {
        const found = member.allWorkPlaces.find(
            (wordPlace) => wordPlace.isPrimaryWorkplace
        )

        if (found) {
            return found
        }
    }

    return member.allWorkPlaces[0]
}

export function findMyWorkplaces(member: Member): Workplace[] {
    let workPlaces: Workplace[] = []

    // eslint-disable-next-line no-restricted-syntax
    for (const position of member.positions) {
        const wpNumbers = position.numbers

        // eslint-disable-next-line guard-for-in
        // eslint-disable-next-line no-restricted-syntax
        for (const wpNumber of wpNumbers) {
            const wp = member.allWorkPlaces.find((w) => +w.number === +wpNumber)
            workPlaces = [...workPlaces, ...[wp]]
        }
    }

    return workPlaces
}

export class Member {
    workPlaces: Workplace[] = []

    allWorkPlaces: Workplace[] = []

    isFavorite = false

    numberOfNotes = 0

    status = ''

    memberId = 0

    firstName = ''

    lastName = ''

    fullName = ''

    memberNumber = ''

    birthday: Date | undefined = undefined

    age: number | undefined = undefined

    positions: Position[] = []

    typeOfRepresentatives: TypeOfRepresentative[] = []

    isOwnMember = false

    isAddedByUser = false

    department: Department | undefined = null

    primaryWorkPlace: Workplace | undefined = undefined

    profilePicture = {
        content: '',
        mimeType: '',
    }

    contactInfoList: Contact[] = []

    privateContact: Contact | undefined = undefined

    workContact: Contact | undefined = undefined

    membershipInfo = {
        date: '',
        name: '',
        number: '',
    }

    // Searchables.
    searchable_fullName = ''

    searchable_workPlace = ''

    searchable_birthDay = ''

    searchable_age: number | undefined = undefined

    public static build = (data: IMemberData): Member => {
        const member = new Member()

        member.allWorkPlaces = mapWorkplaces(data.Arbejdssteder)
        member.isFavorite =
            data.ErFavorit !== undefined ? data.ErFavorit : false
        member.numberOfNotes = data.AntalNoter
        member.status = data.OpdaterStatus
        member.positions = data.Stillinger ? mapPositions(data.Stillinger) : []
        member.memberId = data.Medlemsident
        member.memberNumber = data.Medlemsnummer
        member.firstName = data.Fornavn
        member.lastName = data.Efternavn
        member.fullName = data.FuldeNavn
        member.membershipInfo.date = data.Afdeling?.Indmeldelsesdato
        member.membershipInfo.name = data.Afdeling?.Navn
        member.membershipInfo.number = data.Afdeling?.Nummer
        member.department = data.Afdeling
            ? Department.build(data.Afdeling)
            : undefined
        member.isOwnMember =
            data.ErEgetMedlem !== undefined ? data.ErEgetMedlem : false
        member.isAddedByUser =
            data.ErAktivtTilfoejetAfBruger !== undefined
                ? data.ErAktivtTilfoejetAfBruger
                : false
        member.typeOfRepresentatives = data.Tillidshverv
            ? mapTypeOfRepresentatives(data.Tillidshverv)
            : []

        // member.primaryWorkPlace = findPrimaryWorkPlace(member)

        member.profilePicture.content = data.ProfilBillede?.Content

        member.age = data.Alder

        member.contactInfoList = data.KontaktoplysningerList
            ? mapContacts(data.KontaktoplysningerList)
            : []

        // Map Contacts.
        member.privateContact = member.contactInfoList.find(
            (c) => c.contactInfoType === 'Privat'
        )

        member.workContact = member.contactInfoList.find(
            (c) => c.contactInfoType === 'Arbejde'
        )

        member.birthday = data.FoedselsDato
            ? new Date(data.FoedselsDato)
            : undefined

        member.workPlaces = findMyWorkplaces(member)

        // member.workPlaces.forEach((entry, index) => {
        //     member.workPlaces[index] = attachContactInfo(entry, data)
        // })
        member.primaryWorkPlace = findPrimaryWorkPlace(member)
        // member.primaryWorkPlace = attachContactInfo(
        //     member.primaryWorkPlace,
        //     data
        // )

        if (member.primaryWorkPlace) {
            member.searchable_workPlace = member.primaryWorkPlace.name
        }

        /**
         * Searchables.
         */
        if (member.primaryWorkPlace) {
            member.searchable_workPlace = member.primaryWorkPlace.name
        }

        member.searchable_fullName = member.fullName

        member.searchable_age = data.Alder

        const birthday = data.FoedselsDato
            ? new Date(data.FoedselsDato)
            : undefined

        if (birthday) {
            member.searchable_birthDay = formatDate(birthday)
        }

        return member
    }
}

export function mapMembers(data: IMemberData[]): Member[] {
    return data.map((member) => Member.build(member))
}
